import { paymentConfirmMw } from '@svenskaspelab/nova-payment-sport';
import type { DialogConfig } from '@svs/nova-foundation/nextjs/dialog';
import Games from '@svs/ui/react/icons/Games';
import { createElement, type ReactNode } from 'react';

import { getFormattedCurrency } from '../../lib/entry.browser.js';

/**
 * Creates a payment confirmation middleware with the provided showDialog function.
 *
 * @param showDialog Dialog function to show confirmation dialog
 * @returns Payment confirmation middleware
 */
export function paymentConfirm(
  showDialog: (props: DialogConfig, meta?: string[]) => Promise<void>
): ReturnType<typeof paymentConfirmMw> {
  return paymentConfirmMw(
    (paymentContext) =>
      new Promise((resolve, reject) => {
        const { title, gamesMessages, payableItemsCount, customMessage, role } = paymentContext ?? {};

        const messagesToDisplay = [...(gamesMessages ?? []), customMessage].filter(Boolean) as string[];

        const description: ReactNode[] =
          messagesToDisplay.length > 0
            ? messagesToDisplay.map((msg, index) =>
                // eslint-disable-next-line react/no-array-index-key
                createElement('p', { key: index, className: 'text-center lg:text-left' }, msg)
              )
            : [];

        // Only scenario where we don't show game cancellation message is when there's only one game and it's a group/retailer wager.
        if (
          (role && (role === 'Group' || role === 'Retailer') && payableItemsCount && payableItemsCount !== 1) ||
          role === 'Player'
        ) {
          if (description.length > 0) {
            description.push(createElement('br', { key: 'br' }));
          }
          description.push(
            createElement(
              'p',
              { key: 'cancel', className: 'text-center lg:text-left' },
              `${payableItemsCount && payableItemsCount > 1 ? 'Spelen' : 'Spelet'} går att annullera`
            )
          );
        }

        showDialog({
          icon: createElement(Games),
          title: `${title ?? 'Bekräfta betalning'}?`,
          description: description,
          buttons: {
            primary: {
              label: 'Betala',
              action: () => {
                resolve(); // Continue with payment
              },
            },
            secondary: {
              label: 'Avbryt',
              action: () => {
                reject(new Error('Payment cancelled by customer')); // Cancel payment
              },
            },
          },
        }).catch(() => {
          // If the dialog is closed in another way (ESC, click outside, etc.)
          reject(new Error('Payment cancelled by customer'));
        });
      }),
    getFormattedCurrency
  );
}
